// ===========================================
// Add Product to Invoice Dialog
// ===========================================
// Allows admin to bulk-add a product to invoices
// for a target audience (school, age group, program level, class, family)

import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  Radio,
  FormControlLabel,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import {
  Product,
  ProductInvoiceTargetType,
  ProductQuantityMode,
  AddProductToInvoicesInput,
  AddProductToInvoicesPreview,
  AddProductToInvoicesResult,
  formatProductPrice,
} from '../../../api/product.api';
import {
  usePreviewAddProductToInvoices,
  useAddProductToInvoices,
} from '../../../hooks/useProducts';
import { useAgeGroups, useProgramLevels } from '../../../hooks/useAdmin';
import { useLessons } from '../../../hooks/useLessons';
import { useFamilies } from '../../../hooks/useUsers';
import { safeHexColor } from '../../../utils/safeColor';
import { useDefaultDueDate } from '../../../hooks/useInvoices';
import { formatUTCDate } from '../../../utils/dateUtils';

// ===========================================
// TYPES
// ===========================================

interface AddProductToInvoiceDialogProps {
  open: boolean;
  product: Product | null;
  onClose: () => void;
  onSuccess: (result: AddProductToInvoicesResult) => void;
}

// Day labels for lesson display
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ===========================================
// COMPONENT
// ===========================================

export default function AddProductToInvoiceDialog({
  open,
  product,
  onClose,
  onSuccess,
}: AddProductToInvoiceDialogProps) {
  // Form state
  const [targetType, setTargetType] = useState<ProductInvoiceTargetType>('SCHOOL');
  const [targetId, setTargetId] = useState<string>('');
  // FAMILY supports several at once (2026-09-24); the other four target types are
  // inherently single, so they keep using `targetId`.
  const [familyIds, setFamilyIds] = useState<string[]>([]);
  const [quantity, setQuantity] = useState<number>(1);
  const [quantityMode, setQuantityMode] = useState<ProductQuantityMode>('PER_FAMILY');
  const [unitPrice, setUnitPrice] = useState<string>('');
  const [dueDate, setDueDate] = useState<string>('');
  // Left blank, the server uses the school's default (#243): say which day that is.
  const { data: defaultDue } = useDefaultDueDate(open);
  const dueDateHelperText = defaultDue
    ? `Only applies to newly created invoices. Leave blank to use your default: ${formatUTCDate(
        defaultDue.defaultDueDate,
        { day: '2-digit', month: '2-digit', year: 'numeric' }
      )} (${defaultDue.defaultDueDays} ${defaultDue.defaultDueDays === 1 ? 'day' : 'days'})`
    : "Only applies to newly created invoices. Leave blank to use your school's default.";

  // Step state
  const [step, setStep] = useState<'form' | 'preview' | 'success'>('form');
  const [preview, setPreview] = useState<AddProductToInvoicesPreview | null>(null);
  const [result, setResult] = useState<AddProductToInvoicesResult | null>(null);
  const [error, setError] = useState<string>('');

  // Data hooks
  const { data: ageGroups = [] } = useAgeGroups();
  const { data: programLevels = [] } = useProgramLevels();
  const { data: lessons = [] } = useLessons({ isActive: true });
  const { data: families = [] } = useFamilies();

  // Mutation hooks
  const previewMutation = usePreviewAddProductToInvoices();
  const addToInvoicesMutation = useAddProductToInvoices();

  // Reset form when dialog opens
  useEffect(() => {
    if (open && product) {
      setTargetType('SCHOOL');
      setTargetId('');
      setFamilyIds([]);
      setQuantity(1);
      setQuantityMode('PER_FAMILY');
      setUnitPrice(parseFloat(product.price).toFixed(2));
      // Blank: the server applies the school's default (#243); the helper names the day.
      setDueDate('');
      setStep('form');
      setPreview(null);
      setResult(null);
      setError('');
    }
  }, [open, product]);

  // Reset the target selection when the target type changes.
  // No mount guard: every value this sets is ALREADY the initial state, so the mount
  // run is four no-op setStates that React bails out of. I added a `useRef` guard here
  // reasoning it would protect a future prefilled selection — that was wrong, because
  // the open-reset effect above unconditionally sets targetType/targetId/familyIds on
  // every open and would destroy such a prefill first. Removed rather than left as
  // decoration.
  useEffect(() => {
    setTargetId('');
    setFamilyIds([]);
    setPreview(null);
    setError('');
  }, [targetType]);

  // Active filtered lists
  const activeAgeGroups = useMemo(
    () => ageGroups.filter((ag) => ag.isActive),
    [ageGroups]
  );
  const activeProgramLevels = useMemo(
    () => programLevels.filter((pl) => pl.isActive),
    [programLevels]
  );
  const activeLessons = useMemo(
    () => lessons.filter((l) => l.isActive),
    [lessons]
  );
  const activeFamilies = useMemo(
    () => families.filter((f) => !f.deletionStatus || f.deletionStatus === 'ACTIVE'),
    [families]
  );

  // Form validity
  const isFormValid = useMemo(() => {
    if (!product) return false;
    if (quantity < 1) return false;
    if (targetType === 'FAMILY') return familyIds.length > 0;
    if (targetType !== 'SCHOOL' && !targetId) return false;
    return true;
  }, [product, quantity, targetType, targetId, familyIds]);

  // Build input
  const buildInput = (): AddProductToInvoicesInput => {
    const input: AddProductToInvoicesInput = {
      targetType,
      quantity,
      quantityMode,
    };
    if (targetType === 'FAMILY') {
      // Send the ARRAY even for a single family, so this target type has ONE shape
      // on the wire rather than two paths that can drift apart.
      input.targetIds = familyIds;
    } else if (targetType !== 'SCHOOL') {
      input.targetId = targetId;
    }
    const priceNum = parseFloat(unitPrice);
    if (!isNaN(priceNum) && product && priceNum !== parseFloat(product.price)) {
      input.unitPrice = priceNum;
    }
    if (dueDate) {
      input.dueDate = new Date(dueDate).toISOString();
    }
    return input;
  };

  // Handle preview
  const handlePreview = async () => {
    if (!product) return;
    setError('');

    try {
      const data = await previewMutation.mutateAsync({
        productId: product.id,
        data: buildInput(),
      });
      setPreview(data);
      setStep('preview');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load preview';
      setError(message);
    }
  };

  // Handle execute
  const handleExecute = async () => {
    if (!product) return;
    setError('');

    try {
      const data = await addToInvoicesMutation.mutateAsync({
        productId: product.id,
        data: buildInput(),
      });
      setResult(data);
      setStep('success');
      onSuccess(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to add product to invoices';
      setError(message);
    }
  };

  // Handle close
  const handleClose = () => {
    if (previewMutation.isPending || addToInvoicesMutation.isPending) return;
    onClose();
  };

  if (!product) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add to Invoice</DialogTitle>

      <DialogContent dividers>
        {/* Product Summary */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {product.name}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {formatProductPrice(product.price)}
              </Typography>
              <Chip
                label={product.ProductCategory?.name ?? product.category}
                size="small"
                variant="outlined"
              />
              {product.trackInventory && (
                <Typography variant="body2" color="text.secondary">
                  Stock: {product.quantity ?? 0}
                </Typography>
              )}
            </Stack>
          </Box>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {/* Step: Form */}
        {step === 'form' && (
          <Stack spacing={2.5}>
            {/* Target Type Selection */}
            {/* Bordered panel so the audience choice and its dependent picker read
                as ONE step. Without it the radios, the picker and the quantity
                fields all float on the same flat dialog surface, which is what made
                it unclear that clicking beside the dropdown hits a radio. */}
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                bgcolor: 'background.paper',
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Target Audience
              </Typography>
              <RadioGroup
                value={targetType}
                onChange={(e) => setTargetType(e.target.value as ProductInvoiceTargetType)}
              >
                <FormControlLabel value="SCHOOL" control={<Radio size="small" />} label="Entire School" />
                <FormControlLabel value="AGE_GROUP" control={<Radio size="small" />} label="Age Group" />
                <FormControlLabel value="PROGRAM_LEVEL" control={<Radio size="small" />} label="Program Level" />
                <FormControlLabel value="CLASS" control={<Radio size="small" />} label="Class" />
                <FormControlLabel value="FAMILY" control={<Radio size="small" />} label="Family" />
              </RadioGroup>

              {/* Target-specific selector — INSIDE the panel, so the audience choice
                  and the thing it selects are visibly one step. */}
            {targetType === 'AGE_GROUP' && (
              <FormControl fullWidth size="small">
                <InputLabel>Age Group</InputLabel>
                <Select
                  value={targetId}
                  label="Age Group"
                  onChange={(e) => setTargetId(e.target.value)}
                >
                  {activeAgeGroups.map((ag) => (
                    <MenuItem key={ag.id} value={ag.id}>
                      {ag.name}
                      {ag.minAge != null && ag.maxAge != null && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          ({ag.minAge}-{ag.maxAge} yrs)
                        </Typography>
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {targetType === 'PROGRAM_LEVEL' && (
              <FormControl fullWidth size="small">
                <InputLabel>Program Level</InputLabel>
                <Select
                  value={targetId}
                  label="Program Level"
                  onChange={(e) => setTargetId(e.target.value)}
                >
                  {activeProgramLevels.map((pl) => (
                    <MenuItem key={pl.id} value={pl.id}>
                      {pl.displayName || pl.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {targetType === 'CLASS' && (
              <FormControl fullWidth size="small">
                <InputLabel>Class</InputLabel>
                <Select
                  value={targetId}
                  label="Class"
                  onChange={(e) => setTargetId(e.target.value)}
                >
                  {activeLessons.map((lesson) => (
                    <MenuItem key={lesson.id} value={lesson.id}>
                      {/* Teacher sits on the RIGHT, after the class name and time, with a dot
                          in the teacher's own colour — the same identification an admin gets
                          on the Lessons list. ⚠️ The relation is `Instructor`, NOT `Teacher`:
                          `Lesson.Teacher` does not exist in the API payload (measured
                          2026-09-24) even though the frontend type declares BOTH, because
                          `toLessonsWithRelations` is a bare `as unknown as` cast over a
                          Prisma result whose include names it `Instructor`. */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          width: '100%',
                          minWidth: 0,
                        }}
                      >
                        <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                          {lesson.name} ({lesson.dayOfWeek != null ? `${DAY_LABELS[lesson.dayOfWeek]} ${lesson.startTime}` : 'TBD'})
                        </Box>
                        {lesson.Instructor?.User && (
                          <Box
                            component="span"
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}
                          >
                            <Box
                              component="span"
                              aria-hidden
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                // ⚠️ safeHexColor, not a bare `|| fallback`: `sx` is a
                                // real CSS-injection sink (measured — a crafted value
                                // can break out of the rule and emit page-wide CSS).
                                // Teacher.color is nullable and is NULL for every
                                // teacher in the dev seed; all 8 production teachers
                                // have one.
                                backgroundColor: safeHexColor(lesson.Instructor.color),
                              }}
                            />
                            {/* component="span": Typography defaults to <p>, and the
                                Select renders this whole Box as its COLLAPSED VALUE
                                (measured), which would nest a block element inside the
                                value div and fight noWrap on a narrow dialog. */}
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.secondary"
                              noWrap
                            >
                              {lesson.Instructor.User.firstName} {lesson.Instructor.User.lastName}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {targetType === 'FAMILY' && (
              <Autocomplete
                multiple
                disableCloseOnSelect
                size="small"
                options={activeFamilies}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={activeFamilies.filter((f) => familyIds.includes(f.id))}
                onChange={(_, value) => setFamilyIds(value.map((f) => f.id))}
                // The options list sits over a dialog that is itself a raised
                // surface, so the default popper reads as part of the form. Give it
                // its own elevation + border and cap the height, or a long family
                // list runs past the dialog with no visible edge.
                // ⚠️ The options list is the whole UX problem here. By default MUI
                // renders it in a portal at z-index 1300, OUTSIDE the dialog
                // (measured), and lets it flip ABOVE the input when space is tight —
                // straight over the Target Audience radios. A click aimed at the
                // list then lands on a radio instead, switching the audience and
                // discarding every chosen family. Reported on local dev 2026-09-24.
                //
                // `disablePortal` keeps it inside the dialog's scroll container and
                // `flip: false` stops it opening upwards, so it can never cover the
                // radios. The border + elevation make its edge obvious against the
                // dialog surface, which is the other half of the report.
                disablePortal
                slotProps={{
                  popper: {
                    placement: 'bottom-start',
                    modifiers: [{ name: 'flip', enabled: false }],
                  },
                  paper: {
                    elevation: 8,
                    sx: {
                      border: '1px solid',
                      borderColor: 'divider',
                    },
                  },
                }}
                // ⚠️ Cap the list height HERE, not with a nested
                // `& .MuiAutocomplete-listbox` selector inside `paper.sx`: MUI's own
                // class rule wins on specificity, so that override is silently
                // ignored — MEASURED, the computed value stayed at MUI's 40vh
                // default while the code claimed 240px. This MUI version also has no
                // `listbox` slot (slotProps accepts only clearIndicator | paper |
                // popper | popupIndicator), so slotProps is not the lever either.
                ListboxProps={{ style: { maxHeight: 240 } }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={familyIds.length > 1 ? `Families (${familyIds.length})` : 'Family'}
                    placeholder={familyIds.length === 0 ? 'Search families...' : ''}
                    helperText={
                      familyIds.length > 0
                        ? `${familyIds.length} selected — the list stays open so you can pick several`
                        : 'Pick one or more families'
                    }
                  />
                )}
              />
            )}
            </Box>

            <Divider />

            {/* Quantity Mode Selection */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Quantity Applies To
              </Typography>
              <RadioGroup
                value={quantityMode}
                onChange={(e) => setQuantityMode(e.target.value as ProductQuantityMode)}
              >
                <FormControlLabel
                  value="PER_FAMILY"
                  control={<Radio size="small" />}
                  label="Per Family"
                />
                <FormControlLabel
                  value="PER_STUDENT"
                  control={<Radio size="small" />}
                  label="Per Student"
                />
              </RadioGroup>
              <Typography variant="caption" color="text.secondary">
                {quantityMode === 'PER_FAMILY'
                  ? 'Each family receives the specified quantity regardless of student count'
                  : 'Each student receives the specified quantity (families with multiple students get more)'}
              </Typography>
            </Box>

            {/* Options */}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Quantity"
                type="number"
                size="small"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                inputProps={{ min: 1, max: 100 }}
                sx={{ width: 120 }}
              />
              <TextField
                label="Unit Price"
                type="number"
                size="small"
                value={unitPrice}
                onChange={(e) => setUnitPrice(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
                sx={{ flex: 1 }}
              />
            </Stack>

            <TextField
              label="Due Date (for new invoices)"
              type="date"
              size="small"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText={dueDateHelperText}
            />

            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}

        {/* Step: Preview */}
        {step === 'preview' && preview && (
          <Stack spacing={2}>
            <Alert severity="info">
              <Typography variant="body2">
                <strong>{preview.targetDescription}</strong> &mdash;{' '}
                {preview.summary.totalFamilies} {preview.summary.totalFamilies === 1 ? 'family' : 'families'}
                {preview.summary.totalStudents > 0 && (
                  <> ({preview.summary.totalStudents} {preview.summary.totalStudents === 1 ? 'student' : 'students'})</>
                )} affected
                {quantityMode === 'PER_STUDENT' && ' (per student)'}
              </Typography>
            </Alert>

            <Stack direction="row" spacing={3} flexWrap="wrap">
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Draft Invoices to Update
                </Typography>
                <Typography variant="h6">{preview.summary.existingDraftInvoices}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  New Invoices to Create
                </Typography>
                <Typography variant="h6">{preview.summary.newInvoicesToCreate}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Total Quantity
                </Typography>
                <Typography variant="h6">{preview.summary.totalQuantityNeeded}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Total Value
                </Typography>
                <Typography variant="h6">
                  {formatProductPrice(preview.summary.totalValueToAdd)}
                </Typography>
              </Box>
            </Stack>

            {!preview.summary.inventorySufficient && (
              <Alert severity="warning">
                Insufficient stock. Current: {preview.product.currentStock}, Needed:{' '}
                {preview.summary.totalQuantityNeeded}
              </Alert>
            )}

            {preview.families.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Families ({preview.families.length})
                </Typography>
                <Box
                  sx={{
                    maxHeight: 200,
                    overflow: 'auto',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1,
                  }}
                >
                  {preview.families.map((family) => (
                    <Stack
                      key={family.familyId}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ py: 0.5 }}
                    >
                      <Box>
                        <Typography variant="body2">{family.familyName}</Typography>
                        {quantityMode === 'PER_STUDENT' && family.studentCount > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            {family.studentCount} {family.studentCount === 1 ? 'student' : 'students'} &times; {quantity} = {family.effectiveQuantity}
                          </Typography>
                        )}
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {quantityMode === 'PER_STUDENT' && (
                          <Typography variant="body2" color="text.secondary">
                            qty: {family.effectiveQuantity}
                          </Typography>
                        )}
                        <Chip
                          label={family.action === 'UPDATE' ? 'Update Draft' : 'New Invoice'}
                          size="small"
                          color={family.action === 'UPDATE' ? 'primary' : 'secondary'}
                          variant="outlined"
                        />
                      </Stack>
                    </Stack>
                  ))}
                </Box>
              </Box>
            )}

            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}

        {/* Step: Success */}
        {step === 'success' && result && (
          <Stack spacing={2}>
            <Alert severity="success">
              Product added to {result.summary.updated + result.summary.created} invoices
            </Alert>

            <Stack direction="row" spacing={3}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Updated
                </Typography>
                <Typography variant="h6">{result.summary.updated}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="h6">{result.summary.created}</Typography>
              </Box>
              {result.summary.skipped > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Skipped
                  </Typography>
                  <Typography variant="h6" color="warning.main">
                    {result.summary.skipped}
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Total Value
                </Typography>
                <Typography variant="h6">
                  {formatProductPrice(result.summary.totalValueAdded)}
                </Typography>
              </Box>
            </Stack>

            {result.summary.inventoryDeducted > 0 && (
              <Typography variant="body2" color="text.secondary">
                Inventory deducted: {result.summary.inventoryDeducted} units
              </Typography>
            )}

            {result.errors.length > 0 && (
              <Alert severity="warning">
                {result.errors.length} {result.errors.length === 1 ? 'family' : 'families'} skipped:
                <ul style={{ margin: '4px 0 0 0', paddingLeft: 20 }}>
                  {result.errors.slice(0, 5).map((err) => (
                    <li key={err.familyId}>
                      {err.familyName}: {err.error}
                    </li>
                  ))}
                  {result.errors.length > 5 && (
                    <li>...and {result.errors.length - 5} more</li>
                  )}
                </ul>
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        {step === 'form' && (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handlePreview}
              disabled={!isFormValid || previewMutation.isPending}
            >
              {previewMutation.isPending ? (
                <CircularProgress size={20} sx={{ mr: 1 }} />
              ) : null}
              Preview
            </Button>
          </>
        )}

        {step === 'preview' && (
          <>
            <Button onClick={() => setStep('form')}>Back</Button>
            <Button
              variant="contained"
              onClick={handleExecute}
              disabled={
                addToInvoicesMutation.isPending ||
                !preview?.summary.inventorySufficient ||
                preview?.summary.totalFamilies === 0
              }
            >
              {addToInvoicesMutation.isPending ? (
                <CircularProgress size={20} sx={{ mr: 1 }} />
              ) : null}
              Add to Invoices
            </Button>
          </>
        )}

        {step === 'success' && (
          <Button variant="contained" onClick={handleClose}>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
